import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Month names accepted when a date is typed out ("Aug 4", "August 4 2026").
const MONTH_NAMES = ["january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"]

export default class extends Controller {
  static values = {
    format: { type: String, default: "Y-m-d" },
    altFormat: { type: String, default: "m/d/Y" },
    locale: String,
    // A date AND a time (pre-construction's Important dates: "10/19/2026 12:00 PM").
    enableTime: { type: Boolean, default: false }
  }

  connect() {
    this.fp = flatpickr(this.element, {
      dateFormat: this.formatValue,
      enableTime: this.enableTimeValue,
      altInput: true,
      altFormat: this._altFormat(),
      allowInput: true,
      disableMobile: true,
      appendTo: document.body,
      onOpen: (selectedDates, _dateStr, instance) => {
        if (selectedDates.length === 0) {
          instance.jumpToDate(new Date())
        }
      },
      onClose: () => {
        this.element.dispatchEvent(new Event("change", { bubbles: true }))
      }
    })

    // Copy classes from the original input to the alt input for consistent styling
    if (this.fp.altInput) {
      this.fp.altInput.className = this.element.className
      this.fp.altInput.setAttribute("autocomplete", "off")

      // Typed dates (Larry 2026-08-04): flatpickr only accepts the exact
      // altFormat, so "8/4/26", "Aug 4", "2026-08-04" or "08042026" were
      // either dropped or read as something else. This runs after
      // flatpickr's own blur handling and corrects the date whenever the
      // text can be read confidently; anything unreadable is left alone.
      this._onTypedBlur = () => this._acceptTypedDate()
      this._onTypedKey = (e) => { if (e.key === "Enter") this._acceptTypedDate() }
      this.fp.altInput.addEventListener("blur", this._onTypedBlur)
      this.fp.altInput.addEventListener("keydown", this._onTypedKey)
    }
  }

  disconnect() {
    if (this.fp?.altInput) {
      if (this._onTypedBlur) this.fp.altInput.removeEventListener("blur", this._onTypedBlur)
      if (this._onTypedKey) this.fp.altInput.removeEventListener("keydown", this._onTypedKey)
    }
    if (this.fp) this.fp.destroy()
  }

  _acceptTypedDate() {
    const raw = this.fp?.altInput?.value?.trim()
    if (!raw) return
    // Read it the way THIS picker displays dates. The RSO/KPI/headcount
    // report modals show d/m/Y (Brazil), where "06/08/2026" means 6 August —
    // reading it month-first silently turned it into 8 June and the report
    // came out with an inverted, empty range (CPFL, 2026-08-06).
    const parsed = this.constructor.parseTypedDate(raw, this._altFormat())
    if (!parsed) return

    const current = this.fp.selectedDates[0]
    const same = current &&
      current.getFullYear() === parsed.getFullYear() &&
      current.getMonth() === parsed.getMonth() &&
      current.getDate() === parsed.getDate()
    if (!same) this.fp.setDate(parsed, true)
  }

  // Best-effort reading of a hand-typed date. Ambiguous numeric dates follow
  // the picker's OWN display format: m/d/Y reads month-first (the default),
  // d/m/Y reads day-first. Returns a Date, or null meaning "leave whatever
  // flatpickr decided alone".
  // A page can set its own display format; otherwise the person's Date format
  // choice in their profile (<html data-date-order>) decides, then m/d/Y.
  _altFormat() {
    if (this.hasAltFormatValue) return this.altFormatValue
    return document.documentElement.dataset.dateOrder === "dmy" ? "d/m/Y" : this.altFormatValue
  }

  static parseTypedDate(input, altFormat = "m/d/Y") {
    const raw = String(input).trim().toLowerCase()
    if (!raw) return null

    // Day-first whenever the visible format leads with the day.
    const dayFirst = /^d/i.test(String(altFormat).trim())
    const ordered = (a, b) => (dayFirst ? [b, a] : [a, b]) // → [month, day]

    const today = new Date()
    const at = (y, m, d) => {
      const dt = new Date(y, m - 1, d, 12, 0, 0, 0)
      // Reject rolled-over dates (2/31 becoming Mar 3) instead of saving a surprise.
      if (dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
      return dt
    }
    const fullYear = (y) => (y >= 100 ? y : y + (y <= 69 ? 2000 : 1900))

    if (raw === "today") return at(today.getFullYear(), today.getMonth() + 1, today.getDate())
    if (raw === "yesterday" || raw === "tomorrow") {
      const d = new Date(today)
      d.setDate(d.getDate() + (raw === "tomorrow" ? 1 : -1))
      return at(d.getFullYear(), d.getMonth() + 1, d.getDate())
    }

    // 2026-08-04 (ISO)
    let m = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/)
    if (m) return at(+m[1], +m[2], +m[3])

    // 8/4/2026, 8-4-26, 08.04.2026
    m = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/)
    if (m) return at(fullYear(+m[3]), ...ordered(+m[1], +m[2]))

    // 8/4 — assumed this year
    m = raw.match(/^(\d{1,2})[-/.](\d{1,2})$/)
    if (m) return at(today.getFullYear(), ...ordered(+m[1], +m[2]))

    // 08042026 / 080426
    m = raw.match(/^(\d{2})(\d{2})(\d{4}|\d{2})$/)
    if (m) return at(fullYear(+m[3]), ...ordered(+m[1], +m[2]))

    const monthIndex = (word) => MONTH_NAMES.findIndex(n => n.startsWith(word))

    // Aug 4 / August 4, 2026
    m = raw.match(/^([a-z]{3,9})\.?\s+(\d{1,2})(?:\s*,?\s*(\d{2,4}))?$/)
    if (m) {
      const mi = monthIndex(m[1])
      if (mi >= 0) return at(m[3] ? fullYear(+m[3]) : today.getFullYear(), mi + 1, +m[2])
    }

    // 4 Aug 2026
    m = raw.match(/^(\d{1,2})\s+([a-z]{3,9})\.?(?:\s*,?\s*(\d{2,4}))?$/)
    if (m) {
      const mi = monthIndex(m[2])
      if (mi >= 0) return at(m[3] ? fullYear(+m[3]) : today.getFullYear(), mi + 1, +m[1])
    }

    return null
  }
}
